import { ArrowLeft, Flame } from "lucide-react";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";

import { useNavigate } from "react-router-dom";
import { useHomeDataForDate } from "../../hooks/useHomeDataForDate";
import { SUGAR_LOAD_SCORE_BANDS } from "../../utils/healthInsightScoreBands";
import { HealthInsightScoreBandsList } from "../../components/HealthInsights/HealthInsightScoreBandsList";
import { formatHealthInsightScore, hasHealthInsightScore } from "../../utils/healthInsightsMetrics";

const SugarLoadDetail = () => {
  const navigate = useNavigate();

  // Fetch home page data for the chosen date
  const {
    data: homeData,
    isLoading: loading,
    selectedDate,
    formattedSelectedDate,
  } = useHomeDataForDate();

  // Get sugar load data from API
  const insightData = homeData?.["Health Insights"]?.sugar_ratio_daily;

  const status = insightData?.label ?? "Loading...";
  const sugarScore = insightData?.score;
  const sugarScoreText = formatHealthInsightScore(sugarScore);
  const hasScore = hasHealthInsightScore(sugarScore);
  const label = "Sugar Load";

  const getProgressPercentage = () => {
    if (!hasScore) return 0;
    return Math.max(0, Math.min(100, sugarScore!));
  };



  // Get progress bar color based on status
  const getProgressColor = (status: string) => {
    if (status === "Good" || status === "Excellent") {
      return "bg-green-500";
    }
    if (status === "Moderate") {
      return "bg-orange-500";
    }
    if (status === "Needs Improvement" || status === "Poor") {
      return "bg-red-500";
    }
    return "bg-orange-500"; // Default
  };

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="text-center">
          <div className="mb-4 h-12 w-12 animate-spin rounded-full border-4 border-primary border-t-transparent" />
          <p className="text-footnote text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pb-16">
      <div className="mx-auto max-w-md space-y-4 p-4">
        {/* Header */}
        <div className="flex items-center justify-between pt-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(-1)}
            className="rounded-xl"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-title2 font-extrabold text-foreground">
            {formattedSelectedDate} {label || "Sugar Load"}
          </h1>
          <div className="h-10 w-10" />
        </div>

        {/* Today's Stats */}
        <div className="relative overflow-hidden rounded-2xl border border-border/60 bg-white p-5 shadow-lg dark:border-orange-800/30 dark:bg-card">
          <div className="absolute left-0 top-0 h-full w-1.5 bg-gradient-to-b from-orange-400 to-amber-500" />
          
          <div className="mb-6 flex items-center gap-4">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-orange-100 dark:bg-orange-900/40">
              <Flame className="h-6 w-6 text-orange-600 dark:text-orange-400" />
            </div>
            <div>
              <div className="flex flex-wrap items-center gap-2">
                <p className="text-subhead font-semibold text-muted-foreground">Today's Sugar Load</p>
                <span className={`inline-flex shrink-0 items-center rounded-full px-2 py-0.5 text-2xs font-bold uppercase tracking-wider ${
                  !hasScore
                    ? "bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400"
                    : status === "Good" || status === "Excellent"
                    ? "bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-400"
                    : status === "Needs Improvement" || status === "Moderate"
                    ? "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-400"
                    : "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-400"
                }`}>
                  {!hasScore ? "No Data" : status}
                </span>
              </div>
              <div className="mt-1 flex items-baseline gap-2">
                <span className="text-4xl font-extrabold tracking-tight text-foreground">
                  {sugarScoreText ?? "—"}
                </span>
              </div>
            </div>
          </div>

          <div className="rounded-xl bg-gray-50 p-4 dark:bg-gray-800/40">
            <div className="mb-3 flex justify-between text-subhead font-medium">
              <span className="text-muted-foreground">Daily Progress</span>
            </div>
            <div className="relative h-3 w-full overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700">
              <div
                className={`h-full rounded-full transition-all ${getProgressColor(status)}`}
                style={{ width: `${getProgressPercentage()}%` }}
              />
            </div>
          </div>
        </div>

        {/* What is Sugar Load */}
        <Card className="rounded-2xl border-0 p-5 shadow-sm">
          <h3 className="mb-3 text-title3 font-bold text-foreground">
            What is Sugar Load?
          </h3>
          <p className="text-callout leading-relaxed text-muted-foreground">
            Sugar Load measures the percentage of your total carbohydrate intake
            that comes from sugars. A lower sugar-to-carbs ratio indicates a
            healthier diet with more complex carbohydrates, which provides
            sustained energy and better blood sugar control.
          </p>
          <div className="mt-4">
            <p className="mb-2 text-subhead font-semibold text-foreground">Score Ranges</p>
            <HealthInsightScoreBandsList bands={SUGAR_LOAD_SCORE_BANDS} />
          </div>
        </Card>

        {/* Tips to Reduce Sugar Load */}
        <Card className="rounded-2xl border-0 p-5 shadow-sm">
          <h3 className="mb-3 text-title3 font-bold text-foreground">
            Tips to Reduce Sugar Load
          </h3>
          <ul className="space-y-2 text-callout text-muted-foreground">
            <li className="flex items-start gap-2">
              <span className="text-warning mt-0.5">•</span>
              <span>Choose whole grains over refined carbohydrates</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-warning mt-0.5">•</span>
              <span>
                Replace sugary drinks with water or unsweetened beverages
              </span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-warning mt-0.5">•</span>
              <span>Eat whole fruits instead of fruit juices</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-warning mt-0.5">•</span>
              <span>Check labels for hidden sugars in processed foods</span>
            </li>
          </ul>
        </Card>



      </div>
    </div>
  );
};

export default SugarLoadDetail;
